import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Icon } from '../../design-system/icons';
import {
  captureAttributionParams,
  refCodeActual,
  saveSignupAttribution,
} from '../../lib/signupAttribution';
import { getPostLoginDest } from '../../lib/postLoginDest';

/**
 * Formulario de autenticación del onboarding **sin contenedor**: sólo el contenido,
 * para montarlo en los dos envases que existen (ADR-2026-07-30-registro-pantalla-completa):
 *
 * - `variant="sheet"` → dentro de `AuthSheet` (panel izquierdo fijo de **desktop**).
 * - `variant="page"` → pantalla completa de mobile (`pages/Registro.tsx` y `/auth`).
 *
 * Reemplaza a `pages/Auth.tsx` (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real), así
 * que hereda TODO lo que aquella hacía y que este componente no tenía:
 * `?next=` (destino post-login), la pantalla de **confirmación de email** con reenvío
 * y cooldown, y la captura de atribución al montar. Lo que NO se hereda, a propósito:
 * el checkbox obligatorio de términos — el diseño nuevo usa aceptación implícita.
 *
 * Estilos: tokens del DS v3 vía utilidades Tailwind arbitrarias con el hex del token
 * (permiten estados `hover/focus`), documentados con el nombre del token.
 */

export type AuthMode = 'signup' | 'login' | 'forgot' | 'confirmation';

// Términos y condiciones → misma URL a la que redirige hoy `Auth.tsx`.
const TERMS_URL = 'https://estudio-atlas.com/politica-de-privacidad';

const TITLES: Record<AuthMode, string> = {
  signup: 'Crear cuenta',
  login: 'Ingresá a tu cuenta',
  forgot: 'Restablecer contraseña',
  confirmation: 'Confirmá tu email',
};

export interface AuthFormProps {
  /** Modo visible. En el sheet lo maneja `AuthSheets`; en la página, `Registro`. */
  mode: AuthMode;
  onModeChange: (mode: AuthMode) => void;
  /** id del título, para el `aria-labelledby` del contenedor. */
  titleId: string;
  /** Envase donde se monta (define el ancho/padding y el nivel del título). */
  variant?: 'sheet' | 'page';
}

export function AuthForm({ mode, onModeChange, titleId, variant = 'sheet' }: AuthFormProps) {
  const navigate = useNavigate();
  const isPage = variant === 'page';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [resetCooldown, setResetCooldown] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  // Destino post-login (`?next=`), capturado UNA sola vez al montar: si se
  // recalculara en cada navegación, la segunda leería la URL ya sin el param.
  const [postLoginDest] = useState(getPostLoginDest);

  // Limpia el feedback al cambiar de modo (los errores no deben persistir).
  useEffect(() => {
    setError(null);
    setMessage(null);
  }, [mode]);

  // Cooldowns del reset y del reenvío de confirmación (evitan spamear el email).
  useEffect(() => {
    if (!resetCooldown) return;
    const id = setInterval(() => {
      setResetCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resetCooldown]);

  useEffect(() => {
    if (!resendCooldown) return;
    const id = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // Reenvío del correo de confirmación (pantalla `confirmation`).
  const handleResend = async () => {
    const mail = email.trim();
    if (!mail || resendCooldown > 0 || resendLoading) return;
    setError(null);
    setMessage(null);
    setResendLoading(true);
    try {
      const { error: err } = await supabase.auth.resend({ type: 'signup', email: mail });
      if (err) throw err;
      setMessage('Te enviamos un nuevo correo de confirmación.');
      setResendCooldown(60);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'No se pudo reenviar el correo de confirmación',
      );
    } finally {
      setResendLoading(false);
    }
  };

  const switchMode = (next: AuthMode) => {
    setError(null);
    setMessage(null);
    onModeChange(next);
  };

  // Atribución (UTM / ref / cookies de Meta): TODA la lógica vive en
  // `lib/signupAttribution.ts` — este componente no tiene copia propia
  // (ADR-2026-07-31-atribucion-una-sola-implementacion).
  //
  // La captura corre al MONTAR, no al hacer submit: si el usuario aterriza con
  // `?utm_source=…&ref=…`, los datos quedan en localStorage aunque después navegue
  // entre el carrusel y el registro, o tarde en completar el form.
  useEffect(() => {
    captureAttributionParams();
  }, []);

  const handleGoogle = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const { error: err } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth` },
      });
      if (err) throw err;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Error al continuar con Google',
      );
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    const mail = email.trim();
    if (!mail) {
      setError('Ingresá tu correo electrónico');
      return;
    }
    if (mode !== 'forgot' && !password) {
      setError('Ingresá tu contraseña');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        const { error: err } = await supabase.auth.signInWithPassword({
          email: mail,
          password,
        });
        if (err) {
          if (err.message?.includes('Email not confirmed')) {
            throw new Error(
              'Tu email todavía no está confirmado. Revisá tu bandeja de entrada.',
            );
          }
          if (err.message === 'Invalid login credentials') {
            throw new Error('Correo electrónico o contraseña incorrectos');
          }
          throw err;
        }
        // `?next=` del plugin de Premiere; sin él, al home.
        navigate(postLoginDest, { replace: true });
      } else if (mode === 'signup') {
        const refCode = refCodeActual();
        const metadata: Record<string, string> = {};
        if (name.trim()) metadata.full_name = name.trim();
        if (refCode) metadata.ref_code = refCode;
        const { data, error: err } = await supabase.auth.signUp({
          email: mail,
          password,
          ...(Object.keys(metadata).length ? { options: { data: metadata } } : {}),
        });
        if (err) throw err;
        if (data?.user?.id) await saveSignupAttribution(data.user.id);
        if (data?.session) {
          navigate(postLoginDest, { replace: true });
        } else {
          // Sin sesión = Supabase exige confirmar el mail. Pantalla propia con el
          // reenvío del correo, igual que el `Auth.tsx` que este flujo reemplaza.
          onModeChange('confirmation');
        }
      } else {
        // forgot
        if (resetCooldown > 0) {
          setError(`Esperá ${resetCooldown}s antes de pedir otro email.`);
          setLoading(false);
          return;
        }
        await supabase.auth.resetPasswordForEmail(mail, {
          redirectTo: `${window.location.origin}/restablecer-clave`,
        });
        setResetCooldown(60);
        setMessage(
          'Si existe una cuenta con este email, vas a recibir instrucciones para restablecer tu contraseña.',
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ocurrió un error inesperado');
    } finally {
      setLoading(false);
    }
  };

  const feedback = (
    <>
      {error && (
        <div
          role="alert"
          className="w-full rounded-[12px] bg-[#FEE2E2] px-4 py-3 text-[13px] font-[500] leading-[18px] text-[#991B1B]"
        >
          {error}
        </div>
      )}
      {message && (
        <div
          className="w-full rounded-[12px] bg-[#D1FAE5] px-4 py-3 text-[13px] font-[500] leading-[18px] text-[#065F46]"
        >
          {message}
        </div>
      )}
    </>
  );

  // En la pantalla completa el título es el `h1` del documento; dentro del sheet
  // (diálogo) sigue siendo `h2`.
  const Heading = isPage ? 'h1' : 'h2';

  return (
    <div
      // `key={mode}`: al cambiar de modo el bloque se remonta y entra con un fade corto.
      key={mode}
      className={`flex w-full animate-fade-in flex-col gap-6 ${
        isPage
          ? 'mx-auto max-w-[440px] px-5 pb-10'
          : 'p-5 lg:mx-auto lg:my-auto lg:max-w-[440px] lg:px-6 lg:py-16'
      }`}
    >
      <div className="flex flex-col items-center gap-3">
        {/* Isotipo de Estudio Atlas arriba del título — igual en los tres modos. */}
        <img
          src="/images/estudio-atlas-logo-solo.svg"
          alt="Estudio Atlas"
          draggable={false}
          className={`w-auto select-none ${isPage ? 'h-12' : 'h-11 lg:h-14'}`}
        />
        <Heading
          id={titleId}
          className={`text-center font-[600] text-[#18181B] ${
            isPage
              ? 'text-[24px] leading-[30px]'
              : 'text-[20px] leading-[25px] lg:text-[28px] lg:leading-[34px]'
          }`}
        >
          {TITLES[mode]}
        </Heading>
      </div>

      {feedback}

      {mode === 'signup' && (
        <>
          <GoogleButton
            label="Registrarme con Google"
            loading={googleLoading}
            onClick={handleGoogle}
          />
          <OrDivider />
          <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6" noValidate>
            <div className="flex w-full flex-col gap-4">
              <Field
                id="signup-email"
                label="Correo electrónico"
                type="email"
                autoComplete="email"
                placeholder="tu@correo.com"
                value={email}
                onChange={setEmail}
              />
              <PasswordField
                id="signup-password"
                value={password}
                onChange={setPassword}
                show={showPassword}
                onToggle={() => setShowPassword((s) => !s)}
                autoComplete="new-password"
              />
              <Field
                id="signup-name"
                label="Nombre y apellido"
                autoComplete="name"
                placeholder="Tu nombre"
                value={name}
                onChange={setName}
              />
            </div>
            <SubmitButton label="Registrarse con el correo" loading={loading} />
          </form>
          <Prompt
            text="¿Ya tienes una cuenta?"
            actionLabel="Iniciar sesión"
            onAction={() => switchMode('login')}
          />
          <span className="h-px w-full bg-[#E4E4E7]" />
          <p className="text-center text-[12px] font-[500] leading-[17px] text-[#18181B]">
            Al continuar, estás aceptando nuestros{' '}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-[700] text-[#3B82F6] hover:underline"
            >
              términos y condiciones
            </a>
          </p>
        </>
      )}

      {mode === 'login' && (
        <>
          <GoogleButton
            label="Iniciar sesión con Google"
            loading={googleLoading}
            onClick={handleGoogle}
          />
          <OrDivider />
          <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6" noValidate>
            <div className="flex w-full flex-col gap-4">
              <Field
                id="login-email"
                label="Correo electrónico"
                type="email"
                autoComplete="email"
                placeholder="tu@correo.com"
                value={email}
                onChange={setEmail}
              />
              <div className="flex w-full flex-col gap-1.5">
                <PasswordField
                  id="login-password"
                  value={password}
                  onChange={setPassword}
                  show={showPassword}
                  onToggle={() => setShowPassword((s) => !s)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => switchMode('forgot')}
                  className="self-start text-[12px] font-[700] leading-[17px] text-[#3B82F6] hover:underline"
                >
                  Olvidé mi contraseña
                </button>
              </div>
            </div>
            <SubmitButton label="Iniciar sesión" loading={loading} />
          </form>
          <Prompt
            text="¿Todavía no tenes una cuenta?"
            actionLabel="Registrarme"
            onAction={() => switchMode('signup')}
          />
        </>
      )}

      {mode === 'forgot' && (
        <>
          <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6" noValidate>
            <Field
              id="forgot-email"
              label="Correo electrónico"
              type="email"
              autoComplete="email"
              placeholder="tu@correo.com"
              value={email}
              onChange={setEmail}
            />
            <SubmitButton label="Enviar" loading={loading} />
          </form>
          {/* Única salida del modo `forgot` en DESKTOP: ahí el form vive en el panel
              izquierdo fijo del split, que no tiene header donde poner el back ni X de
              cerrar (es `lg:hidden`). En mobile además está el back del header
              (ADR-2026-08-03-back-en-restablecer-contrasena). */}
          <Prompt
            text="¿Te acordaste de tu contraseña?"
            actionLabel="Iniciar sesión"
            onAction={() => switchMode('login')}
          />
        </>
      )}

      {/* Confirmación de email: Supabase creó el usuario pero NO la sesión, así que
          la cuenta no sirve hasta que abra el link del correo. Con reenvío y
          cooldown de 60s, igual que el `Auth.tsx` que este flujo reemplaza. */}
      {mode === 'confirmation' && (
        <>
          <p className="text-center text-[14px] font-[500] leading-[20px] text-[#52525C]">
            Te enviamos un correo a{' '}
            <span className="font-[700] text-[#18181B]">{email.trim()}</span>. Abrí el link
            que te mandamos para activar tu cuenta.
          </p>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0 || resendLoading}
            className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#F4F4F5] px-4 py-4 text-[15px] font-[700] leading-[19px] text-[#18181B] transition-colors hover:bg-[#E4E4E7] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {resendLoading && <Loader2 className="size-4 animate-spin" />}
            {resendCooldown > 0
              ? `Reenviar correo (${resendCooldown}s)`
              : 'Reenviar correo de confirmación'}
          </button>
          <Prompt
            text="¿Te equivocaste de correo?"
            actionLabel="Volver al registro"
            onAction={() => switchMode('signup')}
          />
        </>
      )}
    </div>
  );
}

// ─── Primitivos de formulario (tokens del DS) ────────────────────────────────

/** Fila de input bordeada (Stroke/Strong #D4D4D8, radius-md), con focus de marca. */
function FieldRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center rounded-[12px] border border-[#D4D4D8] px-4 transition-colors focus-within:border-[#1644EB] focus-within:ring-2 focus-within:ring-[#1644EB]/15">
      {children}
    </div>
  );
}

/**
 * 16px en mobile, 14px (el del diseño) recién en `md:`.
 *
 * No es una preferencia estética: Safari en iOS hace **auto-zoom** al enfocar un input
 * con `font-size` menor a 16px, y ese zoom escala el viewport visual sin que el layout
 * se entere — la pantalla queda ampliada, corrida y con media pantalla en blanco (el
 * contenedor sigue midiendo el alto sin zoom). Es exactamente el síntoma reportado al
 * tocar el campo de contraseña. 16px es el umbral: con eso iOS no interviene.
 * Ver ADR-2026-08-03-inputs-16px-en-mobile-sin-auto-zoom.
 */
const INPUT_CLASS =
  'min-w-0 flex-1 bg-transparent py-3 text-[16px] font-[400] leading-[22px] md:text-[14px] md:leading-[19px] text-[#18181B] outline-none placeholder:text-[#71717B]';

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
}

function Field({ id, label, value, onChange, type = 'text', placeholder, autoComplete }: FieldProps) {
  return (
    <div className="flex w-full flex-col gap-1.5">
      <label htmlFor={id} className="text-[14px] font-[600] leading-[19px] text-[#18181B]">
        {label}
      </label>
      <FieldRow>
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={INPUT_CLASS}
        />
      </FieldRow>
    </div>
  );
}

interface PasswordFieldProps {
  id: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggle: () => void;
  autoComplete?: string;
}

function PasswordField({ id, value, onChange, show, onToggle, autoComplete }: PasswordFieldProps) {
  return (
    <div className="flex w-full flex-col gap-1.5">
      <label htmlFor={id} className="text-[14px] font-[600] leading-[19px] text-[#18181B]">
        Contraseña
      </label>
      <FieldRow>
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••••••"
          autoComplete={autoComplete}
          className={`${INPUT_CLASS} auth-password-field`}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          aria-pressed={show}
          className="ml-2 shrink-0 text-[#71717B] transition-colors hover:text-[#18181B]"
        >
          {show ? (
            <Icon name="outline/eye-off" size={18} />
          ) : (
            <Icon name="outline/eye" size={18} />
          )}
        </button>
      </FieldRow>
    </div>
  );
}

function GoogleButton({
  label,
  loading,
  onClick,
}: {
  label: string;
  loading: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="flex w-full items-center justify-center gap-3 rounded-[12px] bg-[#F4F4F5] px-4 py-4 text-[16px] font-[700] leading-[21px] text-[#18181B] transition-colors hover:bg-[#E4E4E7] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading ? (
        <Loader2 className="size-[18px] animate-spin text-[#71717B]" />
      ) : (
        <Icon name="color/google" size={18} />
      )}
      {label}
    </button>
  );
}

function SubmitButton({ label, loading }: { label: string; loading: boolean }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#18181B] px-4 py-4 text-[15px] font-[700] leading-[19px] text-[#FAFAFA] transition-colors hover:bg-[#27272A] active:bg-[#09090B] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading && <Loader2 className="size-4 animate-spin" />}
      {label}
    </button>
  );
}

function OrDivider() {
  return (
    <div className="flex w-full items-center gap-3">
      <span className="h-px flex-1 bg-[#E4E4E7]" />
      <span className="text-[12px] font-[500] leading-[17px] text-[#52525C]">o</span>
      <span className="h-px flex-1 bg-[#E4E4E7]" />
    </div>
  );
}

function Prompt({
  text,
  actionLabel,
  onAction,
}: {
  text: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <p className="text-center text-[12px] font-[500] leading-[17px] text-[#18181B]">
      {text}{' '}
      <button
        type="button"
        onClick={onAction}
        className="font-[700] text-[#3B82F6] hover:underline"
      >
        {actionLabel}
      </button>
    </p>
  );
}

export default AuthForm;
